<script setup lang="ts">
  import { reactive, computed, onMounted, onUnmounted } from "vue";
  import { useNotification } from "@ui-kit";
  import { useReadRfid, usePowerRfid } from "@plugins";
  import { useProfileOptions } from "@src/hooks";
  import { type ViewType, getNotFoundItemIds } from "../model";
  import { useCheckBindingBatch } from "../logic";
  import { default as CheckBinding } from "./CheckBinding.vue";
  import { default as FoundItemList } from "./FoundItemList.vue";
  import { default as NotBindingItemList } from "./NotBindingItemList.vue";
  import { default as NotBindingDialog } from "./NotBindingDialog.vue";
  import { default as ExistZoneDialog } from "./ExistZoneDialog.vue";

  const { zone_path, zone_id, item_ids } = defineProps<{
    zone_path: string | null;
    zone_id: string;
    item_ids: string[];
  }>();
  const emit = defineEmits<{
    (e: "close", payload: { unlinked_item_ids: string[]; linked_item_ids: string[] }): void;
  }>();

  const state = reactive<{ current_view: ViewType }>({
    current_view: "check_binding",
  });

  const unlinked_item_ids = reactive<string[]>([]);
  const linked_item_ids = reactive<string[]>([]);

  const filtered_item_ids = computed(() =>
    [...item_ids, ...linked_item_ids].filter((id) => !unlinked_item_ids.includes(id)),
  );

  const filtered_found_item_ids = computed(() =>
    check_binding.state.found_item_ids.filter((id) => !unlinked_item_ids.includes(id)),
  );

  const read_rfid = useReadRfid();
  const power_rfid = usePowerRfid();
  const profile_options = useProfileOptions();
  const check_binding = useCheckBindingBatch();
  const notification = useNotification();

  const handleSelectItemGroup = async (view: ViewType) => {
    if (read_rfid.isScanning.value) {
      notification.show({ title: "Остановите сканирование", type: "warning" });

      return;
    }

    if (view === "found_items") {
      await subscribeSingleShort();
    }

    state.current_view = view;
  };

  const handleBackFromFoundItemList = async () => {
    await subscribeMultiple();

    state.current_view = "check_binding";
  };

  const handleBackFromNotBinding = () => {
    if (linked_item_ids.length) {
      check_binding.moveLinkedToFound([...linked_item_ids]);
    }

    state.current_view = "check_binding";
  };

  const handleLinkNotBindingItem = () => {
    const item_id = check_binding.state.item_id_without_zone_single_scan;

    if (!item_id || linked_item_ids.includes(item_id)) return;

    addToLinked(item_id);
    check_binding.moveItemIdSingleScanToFound();
  };

  const handleLinkItemInZone = () => {
    const item_id = check_binding.state.item_id_in_zone_single_scan;

    if (!item_id || linked_item_ids.includes(item_id)) return;

    addToLinked(item_id);
    check_binding.moveItemIdSingleScanToFound();
  };

  const addToUnlinked = (item_id: string) => {
    const index = linked_item_ids.indexOf(item_id);
    if (index !== -1) linked_item_ids.splice(index, 1);

    check_binding.deleteItemFromFoundById(item_id);
    unlinked_item_ids.push(item_id);
  };

  const addToLinked = (item_id: string) => {
    const index = unlinked_item_ids.indexOf(item_id);
    if (index !== -1) unlinked_item_ids.splice(index, 1);

    linked_item_ids.push(item_id);
  };

  const subscribeMultiple = async () => {
    await power_rfid.set(profile_options.profile.value.constants.rfid.power_multi_read);
    read_rfid.setMethod("multiple");
    read_rfid.subscribe((data) => check_binding.processingScanData(data.value));
  };

  const subscribeSingleShort = async () => {
    await power_rfid.set(profile_options.profile.value.constants.rfid.power_once_read);
    read_rfid.setMethod("singleShort");
    read_rfid.subscribe((data) => check_binding.processingSingleScanData(data.value));
  };

  onMounted(async () => {
    check_binding.setZoneId(zone_id);

    await subscribeMultiple();
  });

  onUnmounted(async () => {
    await power_rfid.set(power_rfid.prevPower.value);
    await read_rfid.setMethod("singleShort");

    read_rfid.unsubscribe();
  });
</script>

<template>
  <div class="check-binding-item-in-zone" data-testid="check-binding-item-in-zone">
    <CheckBinding
      v-if="state.current_view === 'check_binding'"
      :zone_path="zone_path"
      :total_item_count="filtered_item_ids.length"
      :found_item_count="check_binding.state.found_item_ids.length"
      :not_binding_item_count="check_binding.state.not_binding_item_ids.length"
      :is_scanning="read_rfid.isScanning.value"
      @close="emit('close', { unlinked_item_ids: [...unlinked_item_ids], linked_item_ids: [...linked_item_ids] })"
      @click:item="handleSelectItemGroup"
    />
    <FoundItemList
      v-else-if="state.current_view === 'found_items'"
      :zone_path="zone_path"
      :zone_id="zone_id"
      :not_found_item_ids="getNotFoundItemIds(filtered_item_ids, check_binding.state.found_item_ids)"
      :found_item_ids="filtered_found_item_ids"
      @click:back="handleBackFromFoundItemList"
      @unlink="addToUnlinked"
    />
    <NotBindingItemList
      v-else-if="state.current_view === 'not_binding_items'"
      :zone_id="zone_id"
      :zone_path="zone_path"
      :item_ids="check_binding.state.not_binding_item_ids"
      @click:back="handleBackFromNotBinding"
      @link="addToLinked"
    />
  </div>
  <NotBindingDialog
    :item_id="check_binding.state.item_id_without_zone_single_scan"
    :zone_id="zone_id"
    @close="check_binding.clearItemIdSingleScan"
    @click:link="handleLinkNotBindingItem"
  />
  <ExistZoneDialog
    :item_id="check_binding.state.item_id_in_zone_single_scan"
    :zone_id="zone_id"
    @close="check_binding.clearItemIdSingleScan"
    @click:link="handleLinkItemInZone"
  />
</template>

<style scoped>
  .check-binding-item-in-zone {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    background-color: var(--sf-base-background-primary);
  }
</style>
